import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useFamily } from '../../contexts/FamilyContext';
import { useAuth } from '../../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faBullseye, faPlus, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import GoalCard from './GoalCard';
import AchievementCard from './AchievementCard';
import CreateGoalModal from './CreateGoalModal';
import AchievementCelebration from './AchievementCelebration';
import { PageHeader, Button } from '../../design-system';

export default function AchievementsPage() {
  const { family, familyId } = useFamily();
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [showCreateGoal, setShowCreateGoal] = useState(false);
  const [celebratingAchievement, setCelebratingAchievement] = useState(null);

  useEffect(() => {
    if (!familyId) return;

    // Load goals
    const goalsQuery = query(
      collection(db, 'families', familyId, 'goals'),
      where('completed', '==', false)
    );
    const unsubscribeGoals = onSnapshot(goalsQuery, (snapshot) => {
      const goalsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setGoals(goalsData);
    });

    // Load achievements
    const achievementsQuery = collection(db, 'families', familyId, 'achievements');
    const unsubscribeAchievements = onSnapshot(achievementsQuery, (snapshot) => {
      const achievementsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAchievements(achievementsData);

      // Check for new achievements that need celebration
      achievementsData.forEach(achievement => {
        if (!achievement.celebrationShown) {
          setCelebratingAchievement(achievement);
          // Mark as shown
          updateDoc(doc(db, 'families', familyId, 'achievements', achievement.id), {
            celebrationShown: true
          });
        }
      });
    });

    return () => {
      unsubscribeGoals();
      unsubscribeAchievements();
    };
  }, [familyId]);

  const activeGoals = goals.filter(g => !g.completed);
  const completedGoals = goals.filter(g => g.completed);

  return (
    <div className="min-h-screen bg-background pt-14 pb-20 md:pt-0 md:pb-0">
      <PageHeader
        title="Achievements & Goals"
        subtitle="Set goals, track progress, and celebrate your family's Jewish journey"
        actions={
          <Button
            onClick={() => setShowCreateGoal(true)}
            className="flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="hidden sm:inline">Create Goal</span>
            <span className="sm:hidden">Create</span>
          </Button>
        }
        maxWidth="1200px"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-8">

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl border-2 border-border p-6">
            <div className="flex items-center gap-3 mb-2">
              <FontAwesomeIcon icon={faBullseye} className="text-2xl text-primary" />
              <div>
                <p className="text-2xl font-bold text-text-primary">{activeGoals.length}</p>
                <p className="text-sm text-text-secondary">Active Goals</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border-2 border-border p-6">
            <div className="flex items-center gap-3 mb-2">
              <FontAwesomeIcon icon={faTrophy} className="text-2xl text-accent" />
              <div>
                <p className="text-2xl font-bold text-text-primary">{achievements.length}</p>
                <p className="text-sm text-text-secondary">Achievements</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border-2 border-border p-6">
            <div className="flex items-center gap-3 mb-2">
              <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-primary-light" />
              <div>
                <p className="text-2xl font-bold text-text-primary">{completedGoals.length}</p>
                <p className="text-sm text-text-secondary">Completed Goals</p>
              </div>
            </div>
          </div>
        </div>

        {/* Active Goals */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-text-primary">Active Goals</h2>
            <button
              onClick={() => setShowCreateGoal(true)}
              className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              Create Goal
            </button>
          </div>
          {activeGoals.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {activeGoals.map(goal => (
                <GoalCard key={goal.id} goal={goal} familyId={familyId} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border-2 border-border p-12 text-center">
              <FontAwesomeIcon icon={faBullseye} className="text-4xl text-text-secondary mb-4" />
              <p className="text-text-secondary mb-4">No active goals yet</p>
              <button
                onClick={() => setShowCreateGoal(true)}
                className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors"
              >
                Create Your First Goal
              </button>
            </div>
          )}
        </div>

        {/* Achievements */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-text-primary mb-4">Achievements</h2>
          {achievements.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {achievements.map(achievement => (
                <AchievementCard key={achievement.id} achievement={achievement} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border-2 border-border p-12 text-center">
              <FontAwesomeIcon icon={faTrophy} className="text-4xl text-text-secondary mb-4" />
              <p className="text-text-secondary">Complete goals to earn achievements!</p>
            </div>
          )}
        </div>

        {/* First-Time Milestones */}
        <div>
          <h2 className="text-2xl font-bold text-text-primary mb-4">First-Time Milestones</h2>
          <div className="bg-white rounded-2xl border-2 border-border p-6">
            <p className="text-text-secondary mb-4">
              Automatically earn badges when you complete experiences for the first time!
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {/* First-time badges would be loaded from experiences */}
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-3xl mb-2">🎉</div>
                <p className="text-xs font-semibold text-text-secondary">First Shabbat</p>
              </div>
              {/* More badges would be dynamically loaded */}
            </div>
          </div>
        </div>
      </div>

      {/* Create Goal Modal */}
      {showCreateGoal && (
        <CreateGoalModal
          familyId={familyId}
          onClose={() => setShowCreateGoal(false)}
        />
      )}

      {/* Achievement Celebration */}
      {celebratingAchievement && (
        <AchievementCelebration
          achievement={celebratingAchievement}
          onClose={() => setCelebratingAchievement(null)}
        />
      )}
    </div>
  );
}
